import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Info,
  GroupAddOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { MainLayout } from "@shared/ui/MainLayout";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { CustomToolbarReloadComponent } from "@shared/ui/MuiTable/CustomToolbarReloadComponent";
import { formatDate, humanReadableTime } from "@shared/utils/getFormatDate";

import { CustomToolbar } from "../components/CustomToolbar";
import { GuestModal } from "../components/GuestModal";
import ReservationDetailModal from "../components/ReservationDetailModal";
import ReservationFlow from "../components/ReservationFlow/ReservationFlow";
import { useReservations } from "../hooks/useReservations";
import { useVacationHomes } from "../hooks/useVacationHomes";
import { useCancelReservation } from "../hooks/useCancelReservation";
import { reservationKeys } from "../api/reservationKeys";
import { RESERVATION_STATUS, RESERVATION_MODE } from "../constants";
import {
  isReservationEditable,
  canCancelReservation,
  daysUntil,
  getStatusChipMeta,
} from "../lib/reservationRules";

export const ManagementReservationPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data = [],
    isFetching,
    refetch,
  } = useReservations();

  const { data: vacationHomes = [] } = useVacationHomes();
  const { mutateAsync: cancelReservation, isPending: isCancelling } =
    useCancelReservation();

  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [openReservationModal, setOpenReservationModal] = useState(false);
  const [reservationMode, setReservationMode] = useState(RESERVATION_MODE.CREATE);
  const [reservationInitialData, setReservationInitialData] = useState(null);
  const [openGuestModal, setOpenGuestModal] = useState(false);
  const [selectedReservationGuests, setSelectedReservationGuests] =
    useState(null);
  const [openInfoModal, setOpenInfoModal] = useState(false);

  const hash = window.location.hash;
  const [, queryString] = hash.split("?") || [];
  const params = new URLSearchParams(queryString);
  const reservationIdParam = params.get("idreserva");

  useEffect(() => {
    if (reservationIdParam && data.length > 0) {
      const reservation = data.find((res) => res.id === String(reservationIdParam));
      if (
        reservation &&
        reservation.status === RESERVATION_STATUS.APPROVED_CEO &&
        reservation.totalPeople > 0
      ) {
        setSelectedReservationGuests(reservation);
        setOpenGuestModal(true);
      } else if (reservation) {
        Swal.fire({
          icon: "error",
          title: t("errorCannotOpenModal"),
          text: t("errorGuestModalNotApprovedCEO"),
        });
      }
    }
  }, [reservationIdParam, data, t]);

  const handleCloseReservationModal = () => {
    if (reservationMode === RESERVATION_MODE.CREATE) {
      setReservationInitialData(null);
    }
    setOpenReservationModal(false);
  };

  const onClickAction = () => {
    setReservationMode(RESERVATION_MODE.CREATE);
    setReservationInitialData(null);
    setOpenReservationModal(true);
  };

  const handleReservationCompleted = () => {
    queryClient.invalidateQueries({ queryKey: reservationKeys.list() });
  };

  const statusChip = (value) => {
    if (!value) {
      return (
        <Chip
          label={t("pendingReviewManager")}
          color="warning"
          style={{ margin: "0 auto" }}
        />
      );
    }

    const { labelKey, color } = getStatusChipMeta(value);
    const label = t(labelKey);

    return (
      <Tooltip title={label}>
        <Chip label={label} color={color} style={{ margin: "0 auto", maxWidth: "80%" }} />
      </Tooltip>
    );
  };

  const handleCancelReservation = async (reservationId) => {
    try {
      const reservation = data.find((res) => res.id === reservationId);
      const daysDifference = daysUntil(reservation.startDate);

      const { isConfirmed } = await Swal.fire({
        title: t("confirmCancelTitle"),
        text:
          daysDifference > 5
            ? t("cancelAdvisoryMessage")
            : t("cancelPenaltyMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("confirmCancelButton"),
        cancelButtonText: t("cancelButton"),
        confirmButtonColor: daysDifference > 5 ? "#4CAF50" : "#d33",
        cancelButtonColor: "#3085d6",
        focusCancel: true,
        customClass: {
          popup: "custom-swal-popup",
          confirmButton:
            daysDifference > 5 ? "no-penalty-btn" : "warning-btn",
          cancelButton: "custom-cancel-btn",
        },
      });

      if (isConfirmed) {
        await cancelReservation({ reservationId });

        Swal.fire({
          title: t("reservationCancelled"),
          text: t("reservationCancelledSuccessfully"),
          icon: "success",
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || t("errorCancellingReservation");
      if (errorMessage === "errorBlockedReservation") {
        Swal.fire({
          title: t("error"),
          text: t("errorBlockedReservation"),
          icon: "error",
        });
      } else {
        Swal.fire({
          title: t("error"),
          text: errorMessage,
          icon: "error",
        });
      }
    }
  };

  const canCancel = canCancelReservation;

  const handleViewDetails = (reservationId) => {
    const reservation = data.find((res) => res.id === reservationId);
    setSelectedReservation(reservation);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedReservation(null);
  };

  const handleEditReservation = (reservationId) => {
    const reservation = data.find((res) => res.id === reservationId);

    const editable = isReservationEditable(reservation);

    if (!editable) {
      Swal.fire({
        icon: "error",
        title: t("errorCannotEditReservationTitle"),
        text: t("errorCannotEditReservationMessage"),
      });
      return;
    }

    setReservationMode(RESERVATION_MODE.EDIT);
    setReservationInitialData(reservation);
    setOpenReservationModal(true);
  };

  const handleOpenGuestModal = (reservationId) => {
    const reservation = data.find((res) => res.id === reservationId);

    if (reservation?.status !== RESERVATION_STATUS.APPROVED_CEO) {
      Swal.fire({
        icon: "error",
        title: t("errorCannotOpenModal"),
        text: t("errorGuestModalNotApprovedCEO"),
      });
      return;
    }

    setSelectedReservationGuests(reservation);
    setOpenGuestModal(true);
  };

  const handleCloseGuestModal = () => {
    setOpenGuestModal(false);
    setSelectedReservationGuests(null);
    navigate("/gestion-de-reservas");
    queryClient.invalidateQueries({ queryKey: reservationKeys.list() });
  };

  const columns = [
    // El DTO expone el nombre del colaborador en `collaborator`; no existe `name`, asi que
    // esta columna venia vacia en todas las filas.
    { field: "collaborator", headerName: t("name"), flex: 1 },
    {
      field: "createdAt",
      headerName: t("createdAt"),
      flex: 1,
      valueFormatter: (value) => {
        const formattedDate = humanReadableTime(value, language);
        return formattedDate !== "Fecha inválida"
          ? formattedDate
          : t("invalidDate");
      },
    },
    { field: "property", headerName: t("property"), flex: 1 },
    {
      field: "startDate",
      headerName: t("startDate"),
      flex: 1,
      valueFormatter: (value) => formatDate(value, t("invalidDate")),
    },
    {
      field: "endDate",
      headerName: t("endDate"),
      flex: 1,
      valueFormatter: (value) => formatDate(value, t("invalidDate")),
    },
    {
      field: "totalPeople",
      headerName: t("guestCount"),
      flex: 1,
      renderCell: (params) => {
        const value = params.value;
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tooltip
              title={
                value === 0
                  ? t("noPeople")
                  : value === 1
                  ? `${value} ${t("person")}`
                  : `${value} ${t("people")}`
              }
            >
              <Chip
                label={
                  value === 0
                    ? t("noPeople")
                    : value === 1
                    ? `${value} ${t("person")}`
                    : `${value} ${t("people")}`
                }
                color="primary"
                style={{ margin: "0 auto", maxWidth: "60%" }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: "assignedRooms",
      headerName: t("roomCount"),
      flex: 1,
      valueGetter: (value, row) => row.assignedRooms?.length ?? 0,
      valueFormatter: (value) =>
        value === 0
          ? t("noRooms")
          : value === 1
          ? `${value} ${t("room")}`
          : `${value} ${t("rooms")}`,
    },
    {
      field: "managerApprovalStatus",
      headerName: t("managerApproval"),
      flex: 1,
      renderCell: (params) =>
        // Prioriza "Cancelada" si el estado general es cancelado
        statusChip(
          params.row.status === RESERVATION_STATUS.CANCELLED
            ? RESERVATION_STATUS.CANCELLED
            : params.value
        ),
    },
    {
      field: "status",
      headerName: t("status"),
      flex: 1,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "actions",
      headerName: t("actions"),
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const reservation = params.row || {};
        const reservationId = reservation?.id;
        const editable = isReservationEditable(reservation);
        const isApprovedByCEO = reservation?.status === RESERVATION_STATUS.APPROVED_CEO;
        const hasGuest = reservation?.totalPeople > 0;

        return (
          <>
            <Tooltip title={t("viewDetails")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => handleViewDetails(reservationId)}
                  title={t("viewDetails")}
                >
                  <Visibility />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                editable
                  ? t("editReservation")
                  : t("errorCannotEditReservationMessage")
              }
            >
              <span>
                <IconButton
                  color="secondary"
                  onClick={() => handleEditReservation(reservationId)}
                  title={t("editReservation")}
                  disabled={!editable}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                isApprovedByCEO
                  ? hasGuest
                    ? t("manageGuests")
                    : t("errorNoGuests")
                  : t("errorGuestModalNotApprovedCEO")
              }
            >
              <span>
                <IconButton
                  color="default"
                  onClick={() => handleOpenGuestModal(reservationId)}
                  title={t("manageGuests")}
                  disabled={!isApprovedByCEO || !hasGuest}
                >
                  <GroupAddOutlined />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t("Cancelar Reservacion")}>
              <span>
                <IconButton
                  color="error"
                  onClick={() => handleCancelReservation(reservationId)}
                  disabled={!canCancel(reservation)}
                  title={
                    canCancel(reservation)
                      ? "Cancelar reserva"
                      : "No se puede cancelar en el estado actual"
                  }
                >
                  <DeleteOutlineOutlined />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const toolbarActions = (
    <>
      <CustomToolbarReloadComponent
        title={t("reloadData")}
        onClickAction={refetch}
      />
      <Tooltip title={t("reservationInfo")}>
        <IconButton
          onClick={() => {
            Swal.fire({
              title: t("reservationStatusInfoTitle"),
              html: `
                <div style="text-align: left; font-family: 'Roboto', sans-serif; font-size: 16px; color: #333; line-height: 1.6;">
                  <p style="margin-bottom: 15px;"><strong>${t("reservationStatusExplanation")}</strong></p>
                  <p style="margin-bottom: 10px;"><strong>${t("pendingReviewManager")}:</strong> ${t("pendingReviewManagerExplanation")}</p>
                  <p style="margin-bottom: 10px;"><strong>${t("approvedManager")}:</strong> ${t("approvedManagerExplanation")}</p>
                  <p style="margin-bottom: 10px;"><strong>${t("rejectedManager")}:</strong> ${t("rejectedManagerExplanation")}</p>
                  <p style="margin-bottom: 10px;"><strong>${t("pendingReviewCEO")}:</strong> ${t("pendingReviewCEOExplanation")}</p>
                  <p style="margin-bottom: 10px;"><strong>${t("approvedCEO")}:</strong> ${t("approvedCEOExplanation")}</p>
                  <p style="margin-bottom: 10px;"><strong>${t("rejectedCEO")}:</strong> ${t("rejectedCEOExplanation")}</p>
                </div>
              `,
              icon: "info",
              confirmButtonText: t("close"),
              background: "#f9f9f9",
              customClass: { popup: "" },
              confirmButtonColor: "#1976d2",
              backdrop: `rgba(0, 0, 0, 0.4)`,
            });
          }}
        >
          <Info />
        </IconButton>
      </Tooltip>

      <CustomToolbar
        title={t("registerReservation")}
        onClickAction={onClickAction}
        t={t}
      />
    </>
  );

  return (
    <MainLayout loading={isFetching || isCancelling}>
      <DataGridTable
        columns={columns}
        rows={data}
        title={t("reservations")}
        language={language}
        height={600}
        toolbarActions={toolbarActions}
      />

      <ReservationDetailModal
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        reservation={selectedReservation}
        t={t}
        statusChip={statusChip}
      />

      <Dialog
        open={openReservationModal}
        onClose={handleCloseReservationModal}
        fullWidth
        maxWidth="md"
      >
        <ReservationFlow
          handleCloseModal={handleCloseReservationModal}
          mode={reservationMode}
          initialData={reservationInitialData}
          vacationHomes={vacationHomes}
          onReservationCompleted={handleReservationCompleted}
        />
      </Dialog>

      <Dialog
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("reservationInfo")}</DialogTitle>
        <DialogContent dividers>
          <Typography>{t("reservationInfoMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfoModal(false)}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      <GuestModal
        open={openGuestModal}
        onClose={handleCloseGuestModal}
        reservation={selectedReservationGuests}
      />
    </MainLayout>
  );
};
